import { useAuth } from "@/components/features/auth/hooks/use-auth";
import { analytics } from "@/lib/analytics/ga4";
import { useLogger } from "@/lib/observability/LoggerContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { setActiveOrg } from "../api/users.client";

interface UseSwitchAccountProps {
  onSuccess?: (orgId?: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for switching the active organization for the current user.
 *
 * @param options - Configuration options for the mutation
 * @returns React Query mutation for switching accounts
 */
export function useSwitchAccount({
  onSuccess,
  onError,
}: UseSwitchAccountProps = {}) {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const logger = useLogger("useSwitchAccount");

  return useMutation({
    mutationKey: ["switch-account"],
    mutationFn: async ({ orgId }: { orgId?: string }) => {
      if (!isAuthenticated) {
        throw new Error("Please login before switching accounts");
      }

      const response = await setActiveOrg({ orgId });
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries that depend on active org
      queryClient.invalidateQueries({
        predicate: (query) => {
          // Invalidate user-related queries and org-related queries
          const queryKey = query.queryKey;
          return (
            queryKey.includes("user") ||
            queryKey.includes("org") ||
            queryKey.includes("active-org")
          );
        },
      });

      onSuccess?.(variables.orgId);
    },
    onError: (error, variables) => {
      logger.error("switchAccount", error, {
        accountId: variables.orgId,
        userId: user?.uid,
      });
      analytics.error(error, "switch_account", "high");
      onError?.(error);
    },
  });
}
